import Vue from "vue";
import { Response } from "@/types";
import { Action } from "@/constants/ajax";
import { UrlHelper } from "@/helpers/UrlHelper";
import { CancelledResponse, Reason } from "@/models/CancelledResponse";
import { ServerResponse } from "@/models/ServerResponse";
import { CustomError } from "@/models/CustomError";
import i18n from "@/i18n";
import { LanguageCodeByBranchCode } from "@/helpers/i18n";
import store from "@/store";
import { CookieHelper } from "@/helpers/CookieHelper";
import { UtilityHelper } from "@/helpers/UtilityHelper";
import { updateTmscCookieAndStore } from "@/helpers/TmscHelper";

/**
 * @class AjaxService Handles ajax calls
 */
export default class AjaxService {
  private readonly proxyRoute: string;
  private readonly formRoute: string;
  private readonly smsRoute: string;
  private readonly documentRoute: string;
  public readonly RESOURCES: any;

  private validationRequests: Map<string, Promise<Response>> = new Map<string, Promise<Response>>();
  private readonly state: any;

  constructor(state: any) {
    this.state = state;
    const token = UrlHelper.getQueryParam("token");

    if (token) {
      this.setToken(token);
    }

    // set up base proxy route
    this.proxyRoute =
      location.href.indexOf("test.tms.pl") > -1 || location.href.indexOf("localhost") > -1
        ? "https://master-proxy.test.tms.pl/realform/" 
        : "https://proxy.tms.pl/realform/";

    // <editor-fold desc="Register available endpoints">
    this.formRoute = this.PROXY_ROUTE + "form-" + this.state.config.version + "/";
    this.smsRoute = this.PROXY_ROUTE + "sms/";
    this.documentRoute = this.PROXY_ROUTE + "document/";

    this.RESOURCES = {
      [Action.GET_ADDRESS]: this.FORM_ROUTE + "get-postal-code-addresses",
      [Action.ABORT_MOBILE]: this.FORM_ROUTE + "skip-desktop-waiting",
      [Action.DICTIONARIES]: this.FORM_ROUTE + "get-dictionaries-list",
      [Action.GET_TOKEN]: this.FORM_ROUTE + "get-token",
      [Action.VALIDATE]: this.FORM_ROUTE + "validate",
      [Action.NEW_ACCOUNT]: this.FORM_ROUTE + "new-account",
      [Action.LOGIN]: this.FORM_ROUTE + "new-login",
      [Action.DATA_UPLOAD_CHOICE]: this.FORM_ROUTE + "data-upload-choice",
      [Action.SELECT_MOBILE_DESKTOP]: this.FORM_ROUTE + "mobile-desktop-choice",
      [Action.DESKTOP_DOCUMENT_SELECTION]: this.FORM_ROUTE + "desktop-document-choice",
      [Action.CHECK_IF_DESKTOP_CAN_PROCEED]: this.FORM_ROUTE + "mobile-desktop-is-completed",
      [Action.GET_IDSCAN_TOKEN]: this.DOCUMENT_ROUTE + "get-idscan-token", // @TODO
      [Action.DESKTOP_DOCUMENT_UPLOAD]: this.DOCUMENT_ROUTE + "desktop-document-upload",
      [Action.MOBILE_SMS]: this.SMS_ROUTE + "mobile-sms",
      [Action.DESKTOP_SMS]: this.SMS_ROUTE + "desktop-sms",
      [Action.VERIFY_EMAIL]: this.FORM_ROUTE + "verify-email",
      [Action.CONFIRM_SMS_CODE]: this.FORM_ROUTE + "desktop-sms-code",
      [Action.MOBILE_DOCUMENT_SELECTION]: this.DOCUMENT_ROUTE + "mobile-document-choice",
      [Action.MOBILE_DOCUMENT_UPLOAD]: this.DOCUMENT_ROUTE + "mobile-document-upload",
      [Action.MOBILE_RETURN_TO_DESKTOP]: this.FORM_ROUTE + "mobile-return-to-desktop", //
      [Action.PERSONAL_DATA_CONFIRM]: this.FORM_ROUTE + "personal-data-confirm",
      [Action.ADDRESS_DETAILS]: this.FORM_ROUTE + "address-details",
      [Action.TAX_OFFICE_DETAILS]: this.FORM_ROUTE + "tax-office-details",
      [Action.CURRENCY]: this.FORM_ROUTE + "currency-choice",
      [Action.RISK_ASSESSMENT]: this.FORM_ROUTE + "risk-assessment",
      [Action.DECLARATION_ACCEPTANCE]: this.FORM_ROUTE + "declaration-acceptance",
      [Action.CLINT_ZONE_LOGIN]: this.FORM_ROUTE + "errors-report",
      [Action.PAYMENT]: this.FORM_ROUTE + "summary-and-payment-choice",
      [Action.ACCOUNT_OPEN]: this.FORM_ROUTE + "account-open",
      [Action.UPLOAD_SUMMARY]: this.FORM_ROUTE + "upload-summary",
      [Action.UPLOAD_TRY_AGAIN]: this.FORM_ROUTE + "upload-try-again",
      [Action.MOBILE_SMS_CODE]: this.FORM_ROUTE + "mobile-sms-code",
      [Action.CONFIRM_EMAIL]: this.FORM_ROUTE + "confirm-email",
    };
    // </editor-fold>
  }

  /**
   * Make GET request to specified url
   * @param url
   * @return Promise<Response>
   */
  public async get(url: string): Promise<Response> {
    const headers = {
      "Content-Type": "application/json",
    };

    const response = (await (Vue as any).http.get(url, { headers })).body;

    if (response.globalError || (response.error && !Array.isArray(response.error))) {
      throw new CustomError(response.globalError || response.error, response.next === "restart");
    }

    return new ServerResponse(response);
  }

  /**
   * Make POST request to specified url
   * @param url
   * @param data Object containing data that should be sent
   * @param customHeaders Object containing request headers override
   *
   * @return ServerResponse
   *
   * @throws GlobalError
   */
  public async post(url: string, data: any = {}, customHeaders: any = {}): Promise<Response> {
    let headers = {
      "Content-Type": "application/json",
    };
    headers = Object.assign(headers, customHeaders);

    if (!this.getToken()) {
      await this.requestToken();

      data.token = this.getToken();
    }

    const response = (await (Vue as any).http.post(url, data, { headers })).body;

    if (response.token && url !== this.RESOURCES[Action.VALIDATE] && url !== this.RESOURCES[Action.DICTIONARIES]) {
      // todo: remove && url !== this.RESOURCES[Action.VALIDATE] after backed is patched
      this.setToken(response.token);
    }

    if (response.globalError || (response.error && !Array.isArray(response.error))) {
      throw new CustomError(response.globalError || response.error, response.next === "restart");
    }

    return new ServerResponse(response);
  }

  /**
   * Make validation request
   * @param name Validated field name
   * @param value Validated value
   * @return Promise<Response>
   */
  public async validate(name: string, value: any): Promise<Response> {
    const promise = this.post(this.RESOURCES[Action.VALIDATE], {
      [name]: value,
      token: this.getToken(),
      languageCode: LanguageCodeByBranchCode[i18n.locale].toUpperCase(),
      branchCode: i18n.locale.toUpperCase(),
    });

    this.validationRequests.set(name, promise);

    let response: Response = await promise;

    // Reject result if another request for this field appeared later
    if (promise !== this.validationRequests.get(name)) {
      response = new CancelledResponse(Reason.RACE);
    }

    return response;
  }

  /**
   * Make request to registered endpoint
   * @param action Endpoint name, must be registered in constructor
   * @param payload Data, may be JS object or FormData instance
   * @param headers Request headers override
   */
  public async runAction(action: Action, payload: any, headers: any = {}) {
    let data: FormData | Record<string, any> | null = null;

    const cookies: string | null = CookieHelper.getCookie("_ga");
    let _ga = "";
    if (cookies) {
      _ga = cookies
        .split(".")
        .slice(-2)
        .join(".");
    }

    if (payload instanceof FormData) {
      data = payload;
      data.append("token", this.getToken() as string);
      data.append("languageCode", LanguageCodeByBranchCode[i18n.locale].toUpperCase());
      data.append("branchCode", i18n.locale === "en" ? "EU" : i18n.locale.toUpperCase());
      data.append("cookie", _ga);
    } else if (action == 7) {
      let cookieGtmParams: string[] | null = null;

      function getCookie(name: string): string | null {
        function escape(s: any) {
          return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, "\\$1");
        }
        const match = document.cookie.match(RegExp("(?:^|;\\s*)" + escape(name) + "=([^;]*)"));
        return match ? match[1] : null;
      }

      if (getCookie("_ga_HLDL6TSEJ9") && cookies) {
        cookieGtmParams = getCookie("_ga_HLDL6TSEJ9")!.split(".");
      }
      if (getCookie("_ga_6PG6QGNWP6") && cookies) {
        cookieGtmParams = getCookie("_ga_6PG6QGNWP6")!.split(".");
      }

      updateTmscCookieAndStore(store, [
        "gclid",
        "gbraid",
        "wbraid",
        "msclkid",
        "gclsrc",
        "adid",
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_content",
        "utm_term",
        "utm_id",
        "cmids",
        "saids",
        "app_instance",
        "gadid",
        "uuid",
        "idfv",
        "idfa",
        "referrer",
      ]);

      const getTmscCookieAsJson = (): Record<string, any> | null => {
        const tmscCookie = document.cookie.split("; ").find(row => row.startsWith("tmsc="));

        if (tmscCookie) {
          try {
            const tmscValue = decodeURIComponent(tmscCookie.split("=")[1]);
            return JSON.parse(tmscValue);
          } catch (error) {
            console.error("Cookie parse error:", error);
          }
        }

        return null;
      };
      const tmscData = getTmscCookieAsJson();
      data = {
        ...payload,
        ...(tmscData ? { tmsc: tmscData } : {}),
        token: this.getToken(),
        session_count: cookieGtmParams ? cookieGtmParams[1] : null,
        session_id: cookieGtmParams ? cookieGtmParams[2] : null,
        isOanda: UtilityHelper.isOandaPath(),
        languageCode: LanguageCodeByBranchCode[i18n.locale].toUpperCase(),
        branchCode: i18n.locale === "en" ? "EU" : i18n.locale.toUpperCase(),
        client_id: _ga,
        cookie: _ga,
      };
    } else {
      data = {
        ...payload,
        token: this.getToken(),
        isOanda: UtilityHelper.isOandaPath(),
        languageCode: LanguageCodeByBranchCode[i18n.locale].toUpperCase(),
        branchCode: i18n.locale === "en" ? "EU" : i18n.locale.toUpperCase(),
        cookie: _ga,
      };
    }

    const response = await this.post(this.RESOURCES[action], data, headers);

    if ((response as ServerResponse).analytics) {
      this.state.dataLayerService.setData((response as any).analytics.userId, (response as any).analytics.leadId);
      this.state.analyticsService.setData({
        transaction_id: (response as any).analytics.leadId,
        userId: (response as any).analytics.userId,
        transaction_id_sha: (response as any).analytics.leadIdSha,
        userIdSha: (response as any).analytics.userIdSha,
      });
    }

    return response;
  }

  /**
   * Retrieve new session token from server
   */
  public async requestToken() {
    const headers = {
      "Content-Type": "application/json",
    };

    const response = (await (Vue as any).http.post(this.RESOURCES[Action.GET_TOKEN], {}, { headers })).body;

    if (response.token) {
      this.setToken(response.token);
    }
  }

  /**
   * Get session token
   */
  public getToken() {
    return window.localStorage.getItem(this.TOKEN);
  }

  /**
   * Set session token
   * @param token
   */
  private setToken(token: string) {
    window.localStorage.setItem(this.TOKEN, token);
  }

  /**
   * Remove token and restart form session
   */
  public restart() {
    window.localStorage.removeItem(this.TOKEN);
  }

  // <editor-fold desc="Getters">

  public get PROXY_ROUTE() {
    return this.proxyRoute;
  }

  public get SMS_ROUTE() {
    return this.smsRoute;
  }

  public get FORM_ROUTE() {
    return this.formRoute;
  }

  public get DOCUMENT_ROUTE() {
    return this.documentRoute;
  }

  private get TOKEN() {
    return "4bdc12c9-69a9-4308-a520-a05d90edd8e2";
  }

  // </editor-fold>
}
